import classNames from 'classnames';
import { Review, WithContext } from 'schema-dts';

import { track } from '@/lib/tracking';
import {
  TestimonialGroup,
  TrackingEventCategory,
  TrackingEventName,
  TrackingModule,
} from '@/lib/types';
import { useGlobalContext } from '@/hooks/use-global-context';
import useMonitorVisibility from '@/hooks/use-monitor-visibility';

import { CardStylesClassNames } from '@/components/card';
import HygraphImage, { HygraphImageProps } from '@/components/hygraph-image';
import Typography from '@/components/typography';

import { CardStyle } from '@/enums/card-style';
import { nomadHealthOrg, TSchemaData } from '@/services/schema-service';

TestimonialGroupRenderer.buildSchema = buildSchema;

function buildSchema(group: TestimonialGroup): Partial<TSchemaData<Review>> {
  const things = group.testimonials.reduce((output, testimonial) => {
    if (!testimonial.name) return output;
    const recommendation: WithContext<Review> = {
      '@context': 'https://schema.org',
      '@type': 'Review',
      name: `${testimonial.name}'s review`,
      itemReviewed: nomadHealthOrg,
      author: { '@type': 'Person', name: testimonial.name },
      reviewBody: testimonial.content,
      datePublished: testimonial.publishedAt,
    };
    return [...output, recommendation];
  }, [] as WithContext<Review>[]);

  return { things };
}

export default function TestimonialGroupRenderer(props: TestimonialGroup) {
  const { pageMeta } = useGlobalContext();

  const [ref] = useMonitorVisibility({
    triggerOnce: () => {
      track({
        pageMeta,
        name: TrackingEventName.ModuleViewed,
        category: TrackingEventCategory.ModuleViewed,
        module: TrackingModule.Testimonial,
        properties: {
          position_number: props.index,
        },
      });
    },
  });

  if (!props.testimonials || props.testimonials.length === 0) return null;
  const testimonial = props.testimonials[0];

  return (
    <div
      id={props.slug ?? props.id}
      ref={ref}
      className={classNames(
        CardStylesClassNames[CardStyle.WHITE_WITH_BORDER],
        'p-4 sm:p-6'
      )}
    >
      <blockquote className='mt-6 md:flex md:flex-grow md:flex-col'>
        <div className='relative text-lg font-medium md:flex-grow'>
          <svg
            width='58'
            height='43'
            viewBox='0 0 58 43'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            className='left-0 top-0 h-8 w-8 text-primary'
          >
            <path
              d='M23.3 0.659999C24.98 0.566661 26.24 0.893327 27.08 1.63999C28.0133 2.38666 28.48 3.36666 28.48 4.58C28.48 5.88667 28.1067 6.86666 27.36 7.51999C26.6133 8.17333 25.4933 8.59333 24 8.77999C19.52 9.24666 16.02 10.32 13.5 12C10.98 13.68 9.72 15.8733 9.72 18.58C9.72 20.0733 10.42 20.6333 11.82 20.26C13.1267 19.8867 14.2933 19.7 15.32 19.7C18.4933 19.7 21.0133 20.82 22.88 23.06C24.7467 25.3 25.68 27.9133 25.68 30.9C25.68 34.0733 24.6533 36.78 22.6 39.02C20.5467 41.26 17.56 42.38 13.64 42.38C9.34667 42.38 6.03333 40.8867 3.7 37.9C1.46 34.82 0.34 30.4333 0.34 24.74C0.34 17.6467 2.34667 12.0467 6.36 7.94C10.3733 3.83333 16.02 1.40666 23.3 0.659999ZM29.6 24.74C29.6 17.6467 31.6067 12.0467 35.62 7.94C39.6333 3.83333 45.28 1.40666 52.56 0.659999C54.24 0.566661 55.5 0.893327 56.34 1.63999C57.2733 2.38666 57.74 3.36666 57.74 4.58C57.74 5.88667 57.3667 6.86666 56.62 7.51999C55.8733 8.17333 54.7533 8.59333 53.26 8.77999C48.78 9.24666 45.28 10.32 42.76 12C40.24 13.68 38.98 15.8733 38.98 18.58C38.98 20.0733 39.68 20.6333 41.08 20.26C42.3867 19.8867 43.5533 19.7 44.58 19.7C47.7533 19.7 50.2733 20.82 52.14 23.06C54.0067 25.3 54.94 27.9133 54.94 30.9C54.94 34.0733 53.9133 36.78 51.86 39.02C49.8067 41.26 46.82 42.38 42.9 42.38C38.6067 42.38 35.2933 40.8867 32.96 37.9C30.72 34.82 29.6 30.4333 29.6 24.74Z'
              fill='#FF4007'
            />
          </svg>
          <Typography.Heading5>{testimonial.content}</Typography.Heading5>
        </div>
        <footer className='mt-8'>
          <div className='flex items-center justify-start'>
            <div className='inline-flex flex-shrink-0 rounded-full border-2 border-white'>
              <div className='h-24 w-24'>
                <HygraphImage
                  sizes='5rem'
                  width={125}
                  height={125}
                  fill={false}
                  quality={99}
                  {...(testimonial.image as HygraphImageProps)}
                  className='rounded-full'
                />
              </div>
            </div>
            <div className='ml-4'>
              <Typography.Subtitle2 className='text-bold' weight='bold'>
                {testimonial.name}
              </Typography.Subtitle2>
              <Typography.Subtitle2>{testimonial.role}</Typography.Subtitle2>
            </div>
          </div>
        </footer>
      </blockquote>
    </div>
  );
}
